import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronRight, Plus } from "lucide-react";

import Seo, { orgLd, websiteLd, faqLd } from "@/components/seo/Seo";
import { fetchProducts, fetchBlogList } from "@/lib/api";
import ProductCard from "@/components/marketing/ProductCard";
import NewsletterSection from "@/components/marketing/NewsletterSection";
import Jar from "@/components/visual/Jar";
import { BRAND, LAB, PURITY_REPORT } from "@/content/brand";
import { HOME_FAQS } from "@/content/faqs";
import { trackViewItemList } from "@/lib/analytics";
import RotatingWords from "@/components/motion/RotatingWords";
import CountingNumber from "@/components/motion/CountingNumber";
import GlassShimmerButton from "@/components/motion/GlassShimmerButton";
import SizeFinder from "@/components/shop/SizeFinder";
import StoryExplorer from "@/components/marketing/StoryExplorer";
import GuideHub from "@/components/shop/GuideHub";
import MoodImage from "@/components/shop/MoodImage";
import { useCatalog } from "@/lib/shop";

const HERO_USES = ["dosa", "tadka", "champi", "baby massage", "fish curry", "the pooja diya"];

const USES = [
  { title: "Everyday cooking", body: "Tadka, poriyal, dosa, appam, sautéed greens and baking. It keeps the coconut aroma your recipes expect.", to: "/recipes", cta: "Cook with it" },
  { title: "Hair champi", body: "The Sunday oiling ritual most of us grew up with. Warm a little, massage into the scalp, wash after an hour or overnight.", to: "/coconut-oil-for-hair", cta: "The hair guide" },
  { title: "Skin and lips", body: "A few drops on dry elbows, heels and lips after a bath, while the skin is still damp.", to: "/coconut-oil-for-skin", cta: "The skin guide" },
  { title: "Baby massage", body: "A pure, single ingredient oil for the daily massage many Indian families swear by. Patch test first.", to: "/coconut-oil-for-baby-massage", cta: "The baby massage guide" },
  { title: "Oil pulling", body: "A spoonful swished for ten minutes each morning, a habit rooted in the Ayurvedic practice of gandusha.", to: "/oil-pulling-with-coconut-oil", cta: "How to do it" },
  { title: "Deep frying", body: "Banana chips, pappadam and vada the Kerala way. How hot to go, and why you should not reuse the oil.", to: "/coconut-oil-for-deep-frying", cta: "Fry with it" },
  { title: "Baking", body: "Solid coconut oil creams like butter; melted, it swaps in for any neutral oil in a cake.", to: "/coconut-oil-for-baking", cta: "Bake with it" },
  { title: "Dry lips", body: "A thin layer at night from a clean fingertip. One ingredient, nothing else in it.", to: "/coconut-oil-for-lips", cta: "The lips guide" },
  { title: "Cracked heels", body: "Warm a little in your palm and rub it in after a bath, then socks on for the night.", to: "/coconut-oil-for-cracked-heels", cta: "The heels guide" },
  { title: "The pooja diya", body: "Coconut oil lamps are part of the evening pooja in many South Indian homes. How to light one well.", to: "/coconut-oil-for-diya", cta: "Lighting the diya" },
  { title: "Makeup remover", body: "A few drops on cotton melt kajal and long wear makeup. Wash your face after.", to: "/coconut-oil-as-makeup-remover", cta: "How to use it" },
];

/** Shows `first` items, then `step` more per tap. Hidden items stay in the HTML. */
function useSeeMore(total, first, step) {
  const [shown, setShown] = useState(first);
  return { shown, more: () => setShown((n) => Math.min(total, n + step)), done: shown >= total };
}

function SeeMoreButton({ onClick, label, shown, total, testId }) {
  return (
    <div className="mt-10 flex flex-col items-center gap-3">
      <button type="button" onClick={onClick} data-testid={testId}
        className="group inline-flex items-center gap-3 rounded-control bg-brand-ink text-white pl-2 pr-6 py-2 font-body text-[15px] font-medium hover:bg-black transition-colors">
        <span className="w-8 h-8 rounded-full bg-white text-brand-ink flex items-center justify-center transition-transform group-hover:rotate-90"><Plus className="w-4 h-4" /></span>
        {label}
      </button>
      <span className="font-body text-[13px] text-ink-muted">{shown} of {total}</span>
    </div>
  );
}

const COMPARE = [
  ["How the oil comes out", "Pressed without added heat", "Expelled, then refined with heat"],
  ["Bleached", "No", "Yes"],
  ["Deodorised", "No, it keeps its natural coconut aroma", "Yes, the aroma is stripped out"],
  ["Where the coconuts grow", "Our family farms in Karnataka", "Rarely disclosed"],
  ["Lab tested", "Every batch; latest sample met all 6 FSSAI purity tests", "Varies by brand"],
];

function useMotionOK() {
  const reduce = useReducedMotion();
  return !reduce && !(typeof window !== "undefined" && window.__PRERENDER__);
}

function Hero() {
  const ref = useRef(null);
  const catalog = useCatalog();
  const from = catalog?.length ? Math.min(...catalog.flatMap((p) => (p.variants || []).map((v) => v.price))) : null;
  const ok = useMotionOK();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} className="relative bg-brand-obsidian text-brand-bone overflow-hidden" data-testid="home-hero">
      <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-10 h-[640px] w-[640px] rounded-full bg-[radial-gradient(circle,rgba(233,196,106,0.18),transparent_62%)]" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-[var(--space-fluid-xl)] pb-[var(--space-fluid-lg)] grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center">
        <div>
          <p className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-brand-bone/60">Karijeeva Cold Pressed Coconut Oil</p>
          <motion.h1
            style={ok ? { opacity: fade } : undefined}
            className="font-display text-white mt-4 text-5xl leading-[1] tracking-heading"
          >
            Pure coconut.
            <br />
            Nothing else.
          </motion.h1>
          <p className="mt-6 font-body text-lg text-white">
            Cold pressed coconut oil for <RotatingWords words={HERO_USES} className="text-brand-oil font-semibold" />
          </p>
          <p className="mt-3 max-w-xl font-body text-base sm:text-lg leading-relaxed text-brand-bone/70">
            Pressed cold in small batches from coconuts we grow on our family farms in Tiptur and on the Karnataka coast.
            Every batch is tested at an independent food testing lab before it reaches your kitchen.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <GlassShimmerButton to="/products" data-testid="home-hero-shop">
              {from ? `Shop now, from ₹${from}` : "Shop the oil"} <ArrowRight className="w-4 h-4" />
            </GlassShimmerButton>
            <Link to="/lab-reports" className="inline-flex items-center gap-1 h-12 px-5 rounded-control bg-white/10 hover:bg-white/15 backdrop-blur-xl font-body text-base text-white transition-colors duration-sm" data-testid="home-hero-lab">
              See our lab report <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <dl className="mt-12 grid grid-cols-3 max-w-xl divide-x divide-white/15 border-y border-white/15" data-testid="home-hero-proof">
            <div className="py-5 pr-4"><dt className="sr-only">Batches lab tested</dt><dd className="font-display text-white text-3xl tracking-heading"><CountingNumber number={parseInt(BRAND.batchesTested, 10) || 500} suffix="+" /></dd><p className="font-body text-xs sm:text-sm text-brand-bone/60">batches lab tested</p></div>
            <div className="py-5 px-4"><dt className="sr-only">FSSAI purity tests passed</dt><dd className="font-display text-white text-3xl tracking-heading"><CountingNumber number={PURITY_REPORT.rows.length} />/{PURITY_REPORT.rows.length}</dd><p className="font-body text-xs sm:text-sm text-brand-bone/60">FSSAI purity tests met</p></div>
            <div className="py-5 pl-4"><dt className="sr-only">Ingredients</dt><dd className="font-display text-white text-3xl tracking-heading"><CountingNumber number={1} /></dd><p className="font-body text-xs sm:text-sm text-brand-bone/60">ingredient: coconut</p></div>
          </dl>
        </div>
        <motion.div style={ok ? { y, scale } : undefined} className="relative mx-auto w-[240px] sm:w-[320px] lg:w-[400px] origin-center">
          <Jar className="w-full h-auto" />
        </motion.div>
      </div>
    </section>
  );
}

/** Words light up one by one as the statement scrolls through the viewport. */
function Statement({ text }) {
  const ref = useRef(null);
  const ok = useMotionOK();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.4"] });
  const words = text.split(" ");
  return (
    <section ref={ref} className="bg-brand-parchment" data-testid="home-statement">
      <p className="max-w-4xl mx-auto px-6 py-28 sm:py-40 font-display text-[clamp(1.75rem,4vw,3.25rem)] leading-[1.12] tracking-heading text-brand-ink">
        {words.map((w, i) => (
          <Word key={i} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} ok={ok}>
            {w}
          </Word>
        ))}
      </p>
    </section>
  );
}
function Word({ children, progress, range, ok }) {
  const opacity = useTransform(progress, range, [0.18, 1]);
  return (
    <motion.span style={ok ? { opacity } : undefined} className="inline-block mr-[0.25em]">
      {children}
    </motion.span>
  );
}

function Reveal({ children, className = "", delay = 0, hidden = false }) {
  const ok = useMotionOK();
  if (!ok || hidden) return <div className={className} hidden={hidden}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 0.61, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionHead({ eyebrow, title, sub, dark = false, center = true }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      {eyebrow && <p className={`font-body text-[15px] font-semibold ${dark ? "text-brand-bone/70" : "text-brand-gold"}`}>{eyebrow}</p>}
      <h2 className={`font-display mt-2 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02] tracking-display ${dark ? "text-white" : "text-brand-ink"}`}>
        {title}
      </h2>
      {sub && <p className={`mt-5 font-body text-[17px] sm:text-[19px] leading-relaxed ${dark ? "text-brand-bone/70" : "text-ink-muted"}`}>{sub}</p>}
    </div>
  );
}

function ClearAndWhite() {
  const [cold, setCold] = useState(false);
  return (
    <section className="bg-brand-parchment py-24 sm:py-32" data-testid="home-states">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <SectionHead
            center={false}
            eyebrow="How pure oil behaves"
            title="Clear when it's warm. White when it's cool."
            sub="Pure coconut oil sets solid and white below about 24°C and turns clear again as it warms. So if your jar looks like snow on a December morning, nothing is wrong. That's just coconut, and it's why we pack it in a wide jar: scoop what you need with a clean, dry spoon."
          />
          <div className="mt-8 inline-flex rounded-full bg-brand-parchment-soft p-1" role="group" aria-label="Show oil at temperature">
            {[["Warm day", false], ["Cool morning", true]].map(([label, val]) => (
              <button
                key={label}
                type="button"
                onClick={() => setCold(val)}
                aria-pressed={cold === val}
                className={`rounded-control px-5 py-2 font-body text-[14px] transition-colors ${cold === val ? "bg-white text-brand-ink shadow-sm" : "text-ink-muted"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="relative mx-auto w-[220px] sm:w-[260px] rounded-tile bg-brand-obsidian p-8">
          <Jar state={cold ? "solid" : "liquid"} className="w-full h-auto transition-all duration-700" />
        </div>
      </div>
    </section>
  );
}

function Uses() {
  const { shown, more, done } = useSeeMore(USES.length, 3, 3);
  return (
    <section className="bg-brand-parchment-soft py-24 sm:py-32" data-testid="home-uses">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHead eyebrow="One oil, many jobs" title="From the tadka to the champi." sub="Indian homes have trusted coconut oil in the kitchen, on the scalp and on the skin for generations. Here is how one jar gets used." />
        <div className="mt-12 grid sm:grid-cols-2 gap-4 empty:hidden">
          <MoodImage slot="kitchen" alt="A South Indian kitchen with coconut oil and curry leaves" className="h-[260px] sm:h-[340px] overflow-hidden rounded-tile" />
          <MoodImage slot="hair" alt="Warm coconut oil for a hair oiling routine" className="h-[260px] sm:h-[340px] overflow-hidden rounded-tile" />
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {USES.map((u, i) => (
            <Reveal key={u.title} delay={(i % 3) * 0.06} hidden={i >= shown} className="rounded-3xl bg-white p-8 flex flex-col min-h-[230px]">
              <h3 className="font-display text-[26px] tracking-heading text-brand-ink">{u.title}</h3>
              <p className="font-body text-[16px] text-ink-muted mt-3 leading-relaxed">{u.body}</p>
              <Link to={u.to} className="mt-auto pt-6 inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
                {u.cta} <ChevronRight className="w-4 h-4" />
              </Link>
            </Reveal>
          ))}
        </div>
        {!done && <SeeMoreButton onClick={more} label="See more ways to use it" shown={shown} total={USES.length} testId="uses-more" />}
      </div>
    </section>
  );
}

function Compare() {
  return (
    <section className="bg-brand-parchment py-24 sm:py-32" data-testid="home-compare">
      <div className="max-w-5xl mx-auto px-6">
        <SectionHead eyebrow="Cold pressed vs refined" title="Not all coconut oil is the same oil." sub="Refined coconut oil (sold as RBD: refined, bleached, deodorised) is processed to be neutral and cheap. Cold pressed oil is left the way the coconut made it." />
        <div className="mt-14 overflow-x-auto">
          <table className="w-full min-w-[560px] text-left font-body">
            <thead>
              <tr className="border-b border-black/10">
                <th className="py-4 pr-4 font-normal text-ink-muted text-[14px]" scope="col"><span className="sr-only">Compare</span></th>
                <th className="py-4 pr-4 font-display text-[19px] text-brand-ink" scope="col">Karijeeva cold pressed</th>
                <th className="py-4 font-display text-[19px] text-ink-muted" scope="col">Refined (RBD) coconut oil</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map(([k, a, b]) => (
                <tr key={k} className="border-b border-black/10 align-top">
                  <th scope="row" className="py-5 pr-4 font-normal text-ink-muted text-[15px]">{k}</th>
                  <td className="py-5 pr-4 text-brand-ink text-[16px]">{a}</td>
                  <td className="py-5 text-ink-muted text-[16px]">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-10 text-center">
          <Link to="/cold-pressed-vs-refined-coconut-oil" className="inline-flex items-center gap-1 font-body text-[17px] text-brand-gold hover:underline">
            Read the full comparison <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function LabProof() {
  return (
    <section className="bg-brand-obsidian text-brand-bone py-24 sm:py-32" data-testid="home-lab">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <SectionHead
          dark
          center={false}
          eyebrow="Proof, not promises"
          title="Meets the FSSAI standard. On every purity test."
          sub={`We send every batch to an independent food testing lab, ${BRAND.batchesTested} batches so far. Our latest sample was checked against the FSSAI standard for coconut oil on six purity tests, and met every one. Want the report for your own jar? Write to us with the batch number on the label.`}
        />
        <div className="rounded-3xl bg-brand-obsidian-soft p-8">
          <p className="font-body text-[13px] text-brand-bone/60">Report {PURITY_REPORT.ref} · {LAB.name}, {LAB.city}</p>
          <dl className="mt-4 divide-y divide-white/10">
            {PURITY_REPORT.rows.map((r) => (
              <div key={r.param} className="flex items-baseline justify-between gap-4 py-3">
                <dt className="font-body text-brand-bone/80">{r.param}</dt>
                <dd className="text-right">
                  <span className="font-display text-white text-[20px]">{r.value}</span>
                  <span className="block font-body text-[12px] text-brand-bone/55">limit: {r.limit}</span>
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-6 font-body text-[13px] text-brand-bone/60">FSSAI licence {BRAND.fssai}</p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/lab-reports" className="inline-flex items-center gap-1 font-body text-[15px] text-white hover:underline">
              See both reports in full <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/coconut-oil-lab-report-explained" className="inline-flex items-center gap-1 font-body text-[15px] text-white hover:underline">
              What each test means <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Shop({ products }) {
  if (!products.length) return null;
  return (
    <section className="bg-brand-parchment py-24 sm:py-32" data-testid="home-bestsellers">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHead eyebrow="Small batches" title="Choose your jar." sub="We make only as much as our farms grow. Some weeks a batch sells out before the next one is ready, and we'd rather wait than buy coconuts we can't vouch for." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} listName="Home bestsellers" />)}
        </div>
      </div>
    </section>
  );
}

function Faqs() {
  const { shown, more, done } = useSeeMore(HOME_FAQS.length, 4, 4);
  return (
    <section className="bg-brand-parchment-soft py-24 sm:py-32" data-testid="home-faqs">
      <div className="max-w-3xl mx-auto px-6">
        <SectionHead eyebrow="Questions" title="Before you buy." />
        <div className="mt-12 divide-y divide-black/10 border-y border-black/10">
          {HOME_FAQS.map((f, i) => (
            <details key={f.q} className="group py-5" hidden={i >= shown}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6 font-display text-[19px] text-brand-ink">
                {f.q}
                <span className="font-body text-2xl leading-none text-ink-muted transition-transform group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p className="mt-3 font-body text-[16px] leading-relaxed text-ink-muted">{f.a}</p>
            </details>
          ))}
        </div>
        {!done && <SeeMoreButton onClick={more} label="More questions" shown={shown} total={HOME_FAQS.length} testId="faqs-more" />}
        <p className="mt-8 text-center">
          <Link to="/faqs" className="inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
            All questions <ChevronRight className="w-4 h-4" />
          </Link>
        </p>
      </div>
    </section>
  );
}

function Journal({ posts }) {
  if (!posts.length) return null;
  return (
    <section className="bg-brand-parchment py-24" data-testid="home-blog">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h2 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-heading text-brand-ink">From the journal.</h2>
          <Link to="/blog" className="inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
            Read all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link to={`/blog/${post.slug}`} key={post.slug} className="group block rounded-3xl bg-white p-8">
              <p className="font-body text-[13px] font-semibold text-brand-gold">{post.category || "Journal"}</p>
              <h3 className="font-display text-[22px] leading-snug tracking-[-0.02em] text-brand-ink mt-2 group-hover:underline">{post.title}</h3>
              {post.excerpt && <p className="font-body text-[15px] text-ink-muted mt-3 line-clamp-3">{post.excerpt}</p>}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-brand-obsidian text-center py-28 sm:py-36">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="font-display text-white text-[clamp(2.4rem,6vw,4.5rem)] leading-[1] tracking-display">
          Switch to coconut oil you can trace to the farm.
        </h2>
        <p className="mt-6 font-body text-[19px] text-brand-bone/70">Free delivery across India on orders above ₹998.</p>
        <Link to="/products" className="mt-10 inline-flex items-center gap-2 rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[17px] px-7 py-3.5 transition-colors">
          Shop Karijeeva <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true })
      .then((p) => {
        setProducts(p.products || []);
        trackViewItemList("Home bestsellers", p.products || []);
      })
      .catch(() => {});
    fetchBlogList()
      .then((b) => setBlog(b.posts?.slice(0, 3) || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <Seo
        title="Pure Cold Pressed Coconut Oil from Our Own Farms in Karnataka"
        description="Karijeeva cold pressed coconut oil: one ingredient, never refined, bleached or deodorised. Grown on our farms in Tiptur and coastal Karnataka, every batch lab tested. Delivered across India."
        jsonLd={[orgLd(), websiteLd(), faqLd(HOME_FAQS.map((f) => ({ question: f.q, answer: f.a })))]}
      />
      <Hero />
      <Shop products={products} />
      <SizeFinder />
      <Statement text="Refined coconut oil is heated, bleached and deodorised before it is packed. Ours never is. We dry our own coconuts into copra, press it cold, filter the oil and jar it. That is the whole recipe." />
      <StoryExplorer />
      <ClearAndWhite />
      <Uses />
      <Compare />
      <LabProof />
      <GuideHub />
      <Faqs />
      <Journal posts={blog} />
      <NewsletterSection />
      <FinalCta />
    </>
  );
}
